import Vue from "vue";
import { BootstrapVueIcons, BootstrapVue } from "bootstrap-vue";
import VeeValidate, { Validator } from "vee-validate";
import pt_BR from "vee-validate/dist/locale/pt_BR";
import dictionary from "./validator/dictionary";
import ReservationView from "../components/ReservationView";
import CreateReservationView from "../components/CreateReservationView.vue";
import AvailabilityView from "../components/AvailabilityView";
import SchedulingView from "../components/SchedulingView";
import MetricsChart from "../components/MetricsChart";
import TemplateDataEditor from "../components/TemplateData/Editor";
import VoucherConsume from "../components/VoucherConsume";
import IncomeSchedulingView from "../components/IncomeSchedulingView";
import SeatMapEditor from "../components/SeatMap/Editor";
import DirectLink from "../components/DirectLink";
import ReportList from "../components/ReportList";
import SaleTags from "../components/SaleTags";
import BatchSiteBuild from "../components/BatchSiteBuild";

Vue.use(VeeValidate);
Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);

Validator.localize("pt_BR", pt_BR);
Validator.localize(dictionary);

VeeValidate.Validator.extend("empty_cart", {
    getMessage: () => `O carrinho deve possuir pelo menos um item`,
    validate: (value) => !!value?.items?.length,
});

VeeValidate.Validator.extend("min_amount_cents", {
    getMessage: () =>
        `O carrinho não atingiu o valor mínimo para o uso do cupom`,
    validate: (value) => {
        if (!value?.coupon?.min_amount_cents) {
            return true;
        }

        const minAmountCents = parseInt(value.coupon.min_amount_cents);
        const total = value.items.reduce((sum, item) => {
            return (
                sum +
                item.tariffs.reduce(
                    (sum, tariff) =>
                        sum +
                        (tariff.tariff.price_cents ||
                            tariff.tariff.priceCents) *
                            tariff.amount,
                    0
                )
            );
        }, 0);
        return total >= minAmountCents;
    },
});

VeeValidate.Validator.extend("coupon_has_some_valid_cart_item", {
    getMessage: () => `O carrinho não possui itens válidos para o uso do cupom`,
    validate: (value, args) => {
        if (!value?.coupon?.usage_restrictions) {
            return true;
        }

        const currentAppId = args && args.length > 0 ? args[0] : null;
        return couponHasSomeValidCartItem(value.coupon, value.items, currentAppId);
    },
});

export const couponHasSomeValidCartItem = (coupon, items, currentAppId) => {
    const hasOwnProducts = items.some(item => item.product.id_app?.toString() === currentAppId?.toString());
    const hasThirdPartyProducts = items.some(item => item.product.id_app?.toString() !== currentAppId?.toString());

    if ((coupon.usage_type === "general" || coupon.usage_type === "product") && hasThirdPartyProducts) {
        return false;
    }

    return items.some(item => couponIsAppliedToItem(coupon, item, currentAppId));
};

export const couponIsAppliedToItem = (coupon, item, currentAppId) => {
    const usageRestrictions = coupon.usage_restrictions ?? [];

    if (!supportsProductAndAppId(coupon, item, currentAppId)) {
        return false;
    }

    const date = item.date?.date;

    if (!date) {
        return true;
    }
    let dateObj = new Date(date + "T00:00:00");
    const time = item.time?.label;

    if (time) {
        const [hours, minutes] = time.split(":");
        dateObj.setHours(hours);
        dateObj.setMinutes(minutes);
    }

    for (const restriction of usageRestrictions) {
        if (!validateCouponUsageDate(restriction, dateObj)) {
            return false;
        }
    }

    return true;
};

const supportsProductAndAppId = (coupon, item, currentAppId) => {
    const itemProductId = item.product.id?.toString();
    const itemAppId = item.product.id_app?.toString();
    const isThirdPartyProduct = itemAppId !== currentAppId?.toString();

    if (coupon.usage_type === "mixed") {
        return isProductOrAppInCoupon(itemProductId, itemAppId, coupon);
    }

    if (coupon.usage_type === "general") {
        return !isThirdPartyProduct;
    }

    if (coupon.usage_type === "product") {
        const inProductOrApp = isProductOrAppInCoupon(itemProductId, itemAppId, coupon);
        return coupon.constraint_type === "include" ? inProductOrApp : !inProductOrApp;
    }

    return true;
};

const isProductOrAppInCoupon = (productId, appId, coupon) => {
    const hasProducts = coupon.product_ids && coupon.product_ids.length > 0;
    const hasApps = coupon.apps_ids && coupon.apps_ids.length > 0;

    if (!hasProducts && !hasApps) {
        return false;
    }

    if (hasProducts && coupon.product_ids.includes(productId)) {
        return true;
    }

    if (hasApps && coupon.apps_ids.includes(appId)) {
        return true;
    }

    return false;
};

const validateCouponUsageDate = (restriction, date) => {
    switch (restriction.type) {
        case "range":
            if (restriction.from_date && restriction.to_date) {
                return !(
                    date >= new Date(restriction.from_date) &&
                    date <= new Date(restriction.to_date)
                );
            } else if (restriction.from_date) {
                return !(date >= new Date(restriction.from_date));
            } else if (restriction.to_date) {
                return !(date <= new Date(restriction.to_date));
            }
            return true;
        case "date_only":
            const restrictionDate = restriction.date.split("T")[0];
            const dateOnly = date.toISOString().split("T")[0];
            if (restrictionDate !== dateOnly) {
                return true;
            }
            break;
        case "week_days":
            if (!restriction.week_days.includes(date.getDay() + 1)) {
                return true;
            }
            break;
    }

    return false;
};

VeeValidate.Validator.extend("direct-link-payment", {
    getMessage: () => `Você deve selecionar uma forma de pagamento`,
    validate: (value) => {
        if (value?.enablePlannePay) {
            return !!value?.selected?.length;
        }
        return true;
    },
});

VeeValidate.Validator.extend(
    "seat-map-min-tariffs",
    {
        getMessage: () =>
            `Você deve selecionar um número de lugares maior ou igual ao número de tarifas selecionadas`,
        validate: (value, [tariffs]) => {
            const selectedCapacitySum = value.reduce(
                (sum, resource) => sum + (resource.capacity ?? 0),
                0
            );

            const selectedTariffAmountSum = tariffs.reduce(
                (sum, tariff) => sum + tariff.amount,
                0
            );

            return selectedCapacitySum >= selectedTariffAmountSum;
        },
    },
    {
        hasTarget: true,
    }
);

export default () => {
    const ids = {
        "reservation-view-root": [
            ReservationView,
            (data) => ({
                monthSearchUrl: data.reservationDatesUrl,
                dateSearchUrl: data.dateReservationsUrl,
                availableProductsUrl: data.availableProductsUrl,
                storeReservationUrl: data.storeReservationUrl,
                voucherPageUrl: data.voucherPageUrl,
                highlightedReservationUrl: data.highlightedReservationUrl,
                minuteSetInterval: parseInt(data.minuteSetInterval),
                dashboardUrl: data.dashboardUrl,
            }),
        ],
        "create-reservation-view": [
            CreateReservationView,
            (data) => ({
                monthSearchUrl: data.reservationDatesUrl,
                dateSearchUrl: data.dateReservationsUrl,
                availableProductsUrl: data.availableProductsUrl,
                storeReservationUrl: data.storeReservationUrl,
                voucherPageUrl: data.voucherPageUrl,
                highlightedReservationUrl: data.highlightedReservationUrl,
                minuteSetInterval: parseInt(data.minuteSetInterval),
                dashboardUrl: data.dashboardUrl,
            }),
        ],
        "availability-view-root": [
            AvailabilityView,
            (dataset) => {
                const keys = [
                    "availableProductsUrl",
                    "storeAvailabilityUrl",
                    "availabilityDatesUrl",
                    "componentTitle",
                    "dashboardUrl",
                    "appMinuteSetInterval",
                ];
                return keys.reduce(
                    (carry, key) => ({ ...carry, [key]: dataset[key] }),
                    {}
                );
            },
        ],
        "scheduling-view-root": [
            SchedulingView,
            (dataset) => ({
                availableProductsUrl: dataset.availableProductsUrl,
                schedulingDatesUrl: dataset.schedulingDatesUrl,
                componentTitle: dataset.componentTitle,
                minuteSetInterval: parseInt(dataset.minuteSetInterval),
                dashboardUrl: dataset.dashboardUrl,
            }),
        ],
        "metrics-chart-root": [
            MetricsChart,
            (dataset) => ({
                isManager: dataset.isManager === "true",
                metricsUrl: dataset.metricsUrl,
            }),
        ],
        "template-data-view-root": [
            TemplateDataEditor,
            (dataset) => ({
                dataFormatUrl: dataset.dataFormatUrl,
                formatEntitiesUrl: dataset.formatEntitiesUrl,
                templateDataUrl: dataset.templateDataUrl,
                updateTemplateDataUrl: dataset.updateTemplateDataUrl,
                getTemplateImagesUrl: dataset.getTemplateImagesUrl,
                uploadTemplateImageUrl: dataset.uploadTemplateImageUrl,
                newPurchaseFlowEnabled: dataset.newPurchaseFlowEnabled === "true",
            }),
        ],
        "voucher-consume-root": [
            VoucherConsume,
            (dataset) => ({
                voucherSearchUrl: dataset.voucherSearchUrl,
                hiddenSaleLink: dataset.hiddenSaleLink,
                mixpanelToken: dataset.mixpanelToken,
                appEnv: dataset.appEnv,
                canConsume: dataset.canConsume === "1",
                canConsumeExceptionally:
                    dataset.canConsumeExceptionally === "1",
                canConsumeAll: dataset.canConsumeAll === "true",
            }),
        ],
        "income-scheduling-view-root": [
            IncomeSchedulingView,
            (data) => ({
                monthSearchUrl: data.incomeSchedulingDatesUrl,
                dateField: data.dateField,
                dateEquivalence: data.dateEquivalence,
            }),
        ],
        "seat-map-editor-root": [
            SeatMapEditor,
            (dataset) => ({
                getSeatMapUrl: dataset.getSeatMapUrl,
                updateSeatMapUrl: dataset.updateSeatMapUrl,
                uploadImageUrl: dataset.uploadImageUrl,
                getResourcesUrl: dataset.getResourcesUrl,
            }),
        ],
        "direct-link-root": [
            DirectLink,
            (dataset) => ({
                appCode: dataset.appCode,
                appId: dataset.appId,
                type: dataset.type,
                dashboardUrl: dataset.dashboardUrl,
                useDashboardSeatMap: dataset.useDashboardSeatMap === "true",
                enableExtendedCalendar: dataset.enableExtendedCalendar === "true",
            }),
        ],
        "report-list-root": [
            ReportList,
            (dataset) => ({
                reportsUrl: dataset.reportsUrl,
            }),
        ],
        "sale-tag-modal": [
            SaleTags,
            (dataset) => ({
                salePosition: dataset.salePosition,
                saleId: dataset.saleId,
                appCode: dataset.appCode,
            }),
            "class",
        ],
        "batch-site-build-root": [
            BatchSiteBuild,
            (data) => ({
                buildableSitesUrl: data.buildableSitesUrl,
                dashboardUrl: data.dashboardUrl,
            }),
        ],
    };

    for (let key in ids) {
        const [CustomComponent, makeData, keyType] = ids[key];
        let elements;

        if (keyType === "class") {
            elements = document.querySelectorAll(`.${key}`);
        } else {
            elements = [document.getElementById(key)];
        }

        if (!elements[0]) {
            continue;
        }

        for (let el of elements) {
            const data = makeData(el.dataset);

            new Vue({
                el,
                data,
                components: { CustomComponent },
                template: '<CustomComponent v-bind="$data" />',
            });
        }
    }
};
